import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Phone, MoreVertical } from "lucide-react";
import Swal from "sweetalert2";
import Calling from "./calling";
import apiUrls from "../../../config/api-urls";
import tokenService from "../../../services/token-service";
import { useSocket } from "../../../context/socket-context";
import toast from "react-hot-toast";

const Chat = ({ handymanName, handymanAvatar, isLive = true, onCall, onEndChat, participantId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const [showEndChatMenu, setShowEndChatMenu] = useState(false);
  const [isCalling, setIsCalling] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  const { socket } = useSocket();
  const token = tokenService.getToken();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Create or get chat when component mounts
  useEffect(() => {
    const initializeChat = async () => {
      if (!participantId || !token) return;
      
      if (!apiUrls.chat.createOrGet || apiUrls.chat.createOrGet.includes('undefined')) {
        console.error("API URL not configured. Please set REACT_APP_API_URL in .env file");
        toast.error("Chat service not configured. Please contact support.");
        return;
      }
      
      setIsLoadingChat(true);
      try {
        const response = await fetch(apiUrls.chat.createOrGet, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ participantId }),
        });

        const data = await response.json();
        if (response.ok && data.success) {
          setChatId(data.data._id);
          // Get current user ID from token or profile
          const userId = tokenService.getUserId();
          setCurrentUserId(userId);
        } else {
          toast.error(data.message || "Failed to initialize chat");
        }
      } catch (error) {
        console.error("Error initializing chat:", error);
        toast.error("Failed to connect to chat. Please check your connection.");
      } finally {
        setIsLoadingChat(false);
      }
    };

    initializeChat();
  }, [participantId, token]);

  // Fetch messages when chat is opened
  useEffect(() => {
    const fetchMessages = async () => {
      if (!chatId || !isOpen || !token) return;
      
      setIsLoadingMessages(true);
      try {
        const response = await fetch(apiUrls.chat.getChatMessages(chatId), {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        if (response.ok && data.success) {
          const formattedMessages = data.data.messages.map((msg) => ({
            id: msg._id,
            text: msg.content,
            sender: msg.sender._id === currentUserId ? "customer" : "handyman",
            timestamp: new Date(msg.createdAt),
            isRead: msg.isRead,
          }));
          setMessages(formattedMessages);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setIsLoadingMessages(false);
      }
    };

    fetchMessages();
  }, [chatId, isOpen, token, currentUserId]);

  // Socket listener for new messages
  useEffect(() => {
    if (!socket || !chatId) {
      return;
    }

    // Join chat room
    socket.emit("join-chat-room", chatId);

    const handleNewMessage = (message) => {
      // Check if message belongs to this chat (handle both string and object)
      const messageChatId = typeof message.chat === 'object' ? message.chat._id : message.chat;
      if (messageChatId !== chatId && message.chat !== chatId) {
        return;
      }

      // Check if message is from current user (already added locally)
      const messageSenderId = typeof message.sender === 'object' ? message.sender._id : message.sender;
      if (messageSenderId === currentUserId) {
        return;
      }

      const formattedMessage = {
        id: message._id,
        text: message.content,
        sender: "handyman",
        timestamp: new Date(message.createdAt),
        isRead: message.isRead,
      };

      // Check if message already exists to prevent duplicates
      setMessages((prev) => {
        const exists = prev.some(msg => msg.id === message._id);
        if (exists) {
          return prev;
        }
        return [...prev, formattedMessage];
      });
      
      // Mark as read if chat is open
      if (isOpen) {
        markAsRead();
      }
    };

    socket.on("newMessage", handleNewMessage);

    return () => {
      socket.off("newMessage", handleNewMessage);
      socket.emit("leave-chat-room", chatId);
    };
  }, [socket, chatId, currentUserId, isOpen]);

  // Mark messages as read when chat is opened
  useEffect(() => {
    if (isOpen && chatId && token) {
      markAsRead();
    }
  }, [isOpen, chatId]);

  const markAsRead = async () => {
    if (!chatId || !token) return;
    
    try {
      await fetch(apiUrls.chat.markAsRead(chatId), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error("Error marking messages as read:", error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !chatId || !token || isSendingMessage) return;

    const messageContent = inputMessage.trim();
    setInputMessage("");
    setIsSendingMessage(true);

    try {
      const response = await fetch(apiUrls.chat.sendMessage, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          chatId,
          content: messageContent,
          messageType: "text",
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        const formattedMessage = {
          id: data.data._id,
          text: data.data.content,
          sender: "customer",
          timestamp: new Date(data.data.createdAt),
          isRead: data.data.isRead,
        };
        
        // Add message locally immediately
        setMessages((prev) => {
          const exists = prev.some(msg => msg.id === data.data._id);
          if (exists) return prev;
          return [...prev, formattedMessage];
        });
      } else {
        toast.error(data.message || "Failed to send message");
        setInputMessage(messageContent);
      }
    } catch (error) {
      toast.error("Failed to send message");
      setInputMessage(messageContent);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const handleEndChat = async () => {
    setShowEndChatMenu(false);
    
    const result = await Swal.fire({
      title: 'End Chat?',
      text: "Are you sure you want to end this chat? You can start a new chat anytime.",
      icon: 'question',
      width: 500,
      showCancelButton: true,
      confirmButtonColor: '#EFA938',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, end chat',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-[#EFA938] hover:bg-[#d78c1f] text-white px-4 py-2 rounded-lg font-semibold',
        cancelButton: 'bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold'
      }
    });

    if (result.isConfirmed) {
      onEndChat?.();
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-[9999] h-16 w-16 rounded-full bg-[#EFA938] text-white shadow-lg hover:bg-[#d78c1f] transition-all duration-300 flex items-center justify-center ${
          isOpen ? "scale-90" : "scale-100 hover:scale-110"
        }`}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
        {!isOpen && messages.filter((m) => m.sender === "handyman" && !m.read).length > 0 && (
          <span className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-red-500 text-xs flex items-center justify-center">
            {messages.filter((m) => m.sender === "handyman" && !m.read).length}
          </span>
        )}
      </button>

      {/* Chat UI */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-[9998] bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col"
          style={{ width: "20rem", height: "28rem", maxWidth: "calc(100vw - 3rem)", maxHeight: "calc(100vh - 8rem)" }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gradient-to-r from-[#EFA938] to-orange-400 dark:from-[#d78c1f] dark:to-orange-500 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {/* Profile Pic with Live Indicator */}
              <div className="relative flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#EFA938] to-orange-400 flex items-center justify-center text-white font-semibold">
                  {handymanAvatar ? (
                    <img src={handymanAvatar} alt={handymanName} className="h-full w-full rounded-full object-cover" />
                  ) : (
                    handymanName.charAt(0).toUpperCase()
                  )}
                </div>
                {isLive && (
                  <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-green-500 border-2 border-white dark:border-slate-800"></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-white truncate">{handymanName}</p>
                  {isLive && (
                    <span className="text-xs text-white/90 bg-white/20 px-2 py-0.5 rounded-full">Live</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Call Button */}
              <button
                onClick={() => {
                  setIsCalling(true);
                  onCall?.();
                }}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                title="Make a call"
              >
                <Phone className="h-4 w-4" />
              </button>
              {/* More Options */}
              <div className="relative">
                <button
                  onClick={() => setShowEndChatMenu(!showEndChatMenu)}
                  className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                  title="More options"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
                {showEndChatMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowEndChatMenu(false)}
                    ></div>
                    <div className="absolute right-0 top-full mt-2 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-slate-700 z-50 min-w-[120px]">
                      <button
                        onClick={handleEndChat}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        End Chat
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50 dark:bg-slate-900">
            {isLoadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#EFA938] border-r-transparent"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading messages...</p>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <p className="text-sm text-gray-500 dark:text-gray-400">No messages yet. Start the conversation!</p>
              </div>
            ) : (
              <>
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === "customer" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-3 py-2 ${
                        message.sender === "customer"
                          ? "bg-[#EFA938] text-white rounded-br-sm"
                          : "bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-slate-600 rounded-bl-sm"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                      <p className={`text-xs mt-1 ${message.sender === "customer" ? "text-white/70" : "text-gray-500 dark:text-gray-400"}`}>
                        {formatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-2xl rounded-bl-sm px-3 py-2">
                      <div className="flex gap-1">
                        <div className="h-2 w-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                        <div className="h-2 w-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                        <div className="h-2 w-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          {!isCalling && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-b-2xl">
              <div className="flex items-end gap-2">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  rows={1}
                  className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EFA938] focus:border-transparent"
                  style={{ maxHeight: "100px" }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isSendingMessage}
                  className="px-4 py-2 rounded-lg bg-[#EFA938] text-white hover:bg-[#d78c1f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
                >
                  {isSendingMessage ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
          )}

          {/* Calling UI Overlay */}
          {isCalling && (
            <Calling
              handymanName={handymanName}
              handymanAvatar={handymanAvatar}
              onHangUp={() => {
                setIsCalling(false);
                onCall?.();
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Chat;

